import React, {useEffect, useState} from 'react';
import {Prompt, useHistory} from 'react-router-dom';
import {Row} from "reactstrap";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import Button from "../button/button";
import Dialog from "../dialog";

const RouteLeavingGuard = ({
                               when = true,
                               navigate,
                               shouldBlockNavigation,
                               confirmationTitle,
                               confirmationMessage,
                           }) => {
    const history = useHistory();
    const [openPrompt, setOpenPrompt] = useState(false);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);
    const [pendingRoute, setPendingRoute] = useState();
    const title = confirmationTitle ?? "Leave the Page?";
    const message = confirmationMessage ?? "Changes you made may not be saved. Are your sure you want to proceed?";

    /**
     * With each change in values of [pendingRoute] and [confirmedNavigation]:
     * - if the dialog is open, and we have a pending route, we either do the routing or use the provided [navigate]
     * callback.
     */
    useEffect(() => {
        if (!confirmedNavigation || !pendingRoute)
            return;
        if (navigate) {
            navigate(pendingRoute.location, pendingRoute.action);
        } else {
            switch (pendingRoute.action) {
                case "PUSH":
                    history.push(pendingRoute.location);
                    break;
                case "POP":
                    history.goBack();
                    break;
                case "REPLACE":
                    history.replace(pendingRoute.location);
                    break
                default:
                    break;
            }
        }
        setPendingRoute(undefined);
        setConfirmedNavigation(false);
    }, [confirmedNavigation, pendingRoute]);

    /**
     * Adds the confirmation trigger for default browser behaviour on reload or back-button click confirmation
     */
    useEffect(() => {
        if (!when)
            return clearWindowEventListeners;
        window.addEventListener('beforeunload', triggerConfirmationForBeforeunload, {capture: true});
        return clearWindowEventListeners;
    }, [when])

    /**
     * Clears the attached event listeners of the window.
     */
    const clearWindowEventListeners = () => {
        window.removeEventListener('beforeunload', triggerConfirmationForBeforeunload, {capture: true});
    }

    /**
     * Triggers browsers' confirmation popup in case of leaving the page or reloading the page.
     * @param e
     * @return {string}
     */
    const triggerConfirmationForBeforeunload = (e) => {
        e.preventDefault();
        return (e.returnValue = message);
    }

    /**
     * Blocks the navigation of the route by checking whether the route is to be blocked or not. If blocked, shows
     * the confirmation prompt.
     * @param {History.Location} nextLocation
     * @param {History.action} action
     * @return {boolean} whether to allow navigation
     */
    const blockNavigation = (nextLocation, action) => {
        if (!confirmedNavigation && shouldBlockNavigation(nextLocation, action)) {
            setOpenPrompt(true);
            setPendingRoute({location: nextLocation, action});
            return false;
        }
        return true;
    };

    /**
     * Closes the modal and sets the confirmedNavigation to trigger the location change
     */
    const onConfirmNavigation = () => {
        setOpenPrompt(false);
        setConfirmedNavigation(true);
    };

    /**
     * Closes the dialog
     */
    const closeDialog = () => {
        setOpenPrompt(false);
        setPendingRoute(undefined);
    }

    return (
        <>
            <Prompt when={when} message={blockNavigation}/>
            <Dialog
                maxWidth={'sm'}
                open={openPrompt}
                setOpen={closeDialog}
            >
                <DialogTitle className={'pb-0 px-0'}>
                    <Row xs={12} className='no-gutters justify-content-center w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            {title}
                        </p>
                    </Row>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent>
                    <div className={'p-3'}>
                        <p className={'text-sm text-color-2'}>
                            {message}
                        </p>
                    </div>
                    <div className='my-2 w-100'/>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                borderRadius={'0 8px 0 8px'}
                                onClick={onConfirmNavigation}
                            >
                                Leave anyway
                            </Button>
                            <Button
                                variant={'dialog'}
                                borderRadius={'8px 0 8px 0'}
                                onClick={closeDialog}
                            >
                                Stay on this page
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
};


export default RouteLeavingGuard;
